import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { AggregateDataCheckbox as AggregateHiresCheckbox } from '@/components/ui/aggregate-data-checkbox';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useOpeningContext } from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';

export function AggregateHiresControls() {
  const intl = useIntl();
  const openingSlug = useOpeningSlug();
  const {
    showAggregateData,
    setShowAggregateHiresCheckbox,
    currentHiringGoalId,
    showAggregateHires,
    setShowAggregateHires,
  } = useOpeningContext();
  const [open, setOpen] = useState(false);

  const { data } = useSuspenseQuery({
    // Added showAggregateData to the query key to trigger refetches so hiring openings update with everything else.
    queryKey: [
      ...queryKeys.openingDetails.hiringOpenings,
      openingSlug,
      currentHiringGoalId,
      showAggregateData,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsHiringOpenings(
        openingSlug,
        currentHiringGoalId,
      ),
  });

  const hiringOpenings = useMemo(
    () => data.hiring_openings?.filter(o => o.slug !== openingSlug) ?? [],
    [data, openingSlug],
  );

  useEffect(() => {
    if (hiringOpenings.length > 0) {
      setShowAggregateHiresCheckbox(true);
    }
  }, [setShowAggregateHiresCheckbox, hiringOpenings, openingSlug]);

  if (hiringOpenings.length === 0) return null;

  return (
    <div className="flex items-center gap-3">
      <AggregateHiresCheckbox
        checked={showAggregateHires}
        onCheckedChange={value => setShowAggregateHires(!!value)}
        label={intl.formatMessage({
          defaultMessage: 'Include aggregate hires',
        })}
        tooltipContent={intl.formatMessage({
          defaultMessage:
            'This is total number of hires across all linked openings.',
        })}
      />
      {showAggregateHires && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="link" className="h-fit hover:no-underline">
              <FormattedMessage
                defaultMessage="{count} Hiring Openings"
                values={{ count: hiringOpenings.length }}
              />
              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="max-w-100 flex w-fit flex-col items-start justify-center"
          >
            {hiringOpenings.map(opening => (
              <div
                key={opening.external_id}
                className="flex w-full items-center justify-between py-2"
              >
                <Typography variant="subtitle1">{opening.title}</Typography>
              </div>
            ))}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

AggregateHiresControls.Skeleton = function AggregateHiresControlsSkeleton() {
  return (
    <div className="flex items-center gap-3">
      <Skeleton className="h-5 w-[178px]" />
      <Skeleton className="h-5 w-[142px]" />
    </div>
  );
};
